import { useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLS = [["external_order_id", "Codice"], ["customer_name", "Cliente"], ["city", "Località"], ["street", "Via"], ["appointment_date", "Data"], ["appointment_time", "Ora"], ["executing_company", "Squadra"]];
const TYPES = [["DELIVERY", "Delivery"], ["ASSURANCE", "Assurance"], ["CONSTRUCTION", "Construction"]];

const Preview = ({ rows }) => {
  const cols = COLS.filter(([k]) => rows.some((r) => k in r));
  return (
    <div className="mt-2 overflow-x-auto"><table className="w-full text-xs"><thead><tr>{cols.map(([k, l]) => <th key={k} className="whitespace-nowrap border-b px-2 py-1 text-left font-semibold">{l}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i}>{cols.map(([k]) => <td key={k} className="whitespace-nowrap border-b px-2 py-1">{r[k]}</td>)}</tr>)}</tbody></table></div>
  );
};

const PendingCard = ({ p, projects, onDone }) => {
  const [projectId, setProjectId] = useState(p.project_id || "");
  const [type, setType] = useState(p.service_type);
  const [busy, setBusy] = useState(false);
  const act = async (fn, ok) => { setBusy(true); try { const r = await fn(); toast.success(ok(r.data)); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const approve = () => act(() => api.post(`/email-import/pending/${p.id}/approve`, { project_id: projectId, service_type: type }),
    (b) => `Importato: ${b.counts.nuovo} nuovi, ${b.counts.aggiornato} aggiornati, ${b.counts.duplicato} già presenti, ${b.counts.scartato} scartati`);
  const discard = () => act(() => api.post(`/email-import/pending/${p.id}/discard`), () => "Import scartato");
  return (
    <div className="min-w-0 border-b p-3" data-testid={`email-pending-${p.id}`}>
      <div className="text-sm font-semibold">{p.filename} <span className="font-normal text-muted-foreground">· {p.total} righe</span></div>
      <div className="text-xs text-muted-foreground">{fmtDateTime(p.at)} · {p.sender} · {p.subject}</div>
      {p.note && <div className="mt-1 text-xs text-amber-700" data-testid={`email-pending-note-${p.id}`}>{p.note}</div>}
      <Preview rows={p.preview} />
      <div className="mt-2 grid gap-2 sm:grid-cols-[1.5fr_1fr_auto_auto] [&>*]:min-w-0">
        <Select value={projectId || undefined} onValueChange={setProjectId}><SelectTrigger className="bg-white [&>span]:truncate" data-testid={`email-pending-project-${p.id}`}><SelectValue placeholder="Scegli commessa" /></SelectTrigger>
          <SelectContent>{projects.map((x) => <SelectItem key={x.id} value={x.id}>{x.code} · {x.name}</SelectItem>)}</SelectContent></Select>
        <Select value={type} onValueChange={setType}><SelectTrigger className="bg-white" data-testid={`email-pending-type-${p.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
        <Button disabled={busy || !projectId} onClick={approve} data-testid={`email-pending-approve-${p.id}`}><Check className="mr-1 h-4 w-4" />Conferma e importa</Button>
        <Button variant="outline" disabled={busy} onClick={discard} data-testid={`email-pending-discard-${p.id}`}><X className="mr-1 h-4 w-4" />Scarta</Button>
      </div>
    </div>
  );
};

export const EmailPending = ({ items, projects, onDone }) => (
  <div className="mt-6" data-testid="email-pending-list">
    <div className="micro-label mb-1 border-b pb-1">In attesa di conferma ({items.length})</div>
    {items.length === 0 ? <p className="py-2 text-sm text-muted-foreground">Nessun file da confermare.</p>
      : <div className="border bg-white">{items.map((p) => <PendingCard key={p.id} p={p} projects={projects} onDone={onDone} />)}</div>}
  </div>
);
